import {getInitialViewSettings, ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    ExternalWork,
    ExternalWorkId, ExternalWorkProp, ExternalWorkModule
} from "../../models/contractor/ExternalWork";

import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {fetchInitialSelectOptions} from "../common/SelectActions";
import {timesheetTaskSelectRoute} from "../../models/timesheet/TimesheetTask";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "external-work/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type ExternalWorkActionTypes = FetchExternalWorksActionTypes
    | FetchExternalWorkByIdActionTypes
    | CreateExternalWorkActionTypes
    | UpdateExternalWorkActionTypes
    | DeleteExternalWorkActionTypes
    | RestoreExternalWorkActionTypes

/**
 * FETCH EXTERNAL_WORKS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchExternalWorksAction extends ViewIdentifiable, ReduxAction< typeof FETCH_EXTERNAL_WORKS > {}
export interface FetchExternalWorksSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_EXTERNAL_WORKS_SUCCESS > {
    payload: ApiReturnType< ExternalWork[] >
}
export interface FetchExternalWorksErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_EXTERNAL_WORKS_ERROR >{}

export type FetchExternalWorksActionTypes = FetchExternalWorksAction
    | FetchExternalWorksErrorAction
    | FetchExternalWorksSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_EXTERNAL_WORKS = "FETCH_EXTERNAL_WORKS";
export const FETCH_EXTERNAL_WORKS_SUCCESS = "FETCH_EXTERNAL_WORKS_SUCCESS";
export const FETCH_EXTERNAL_WORKS_ERROR = "FETCH_EXTERNAL_WORKS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchExternalWorks( viewIdentifier: string ) : FetchExternalWorksAction {
    return { type: FETCH_EXTERNAL_WORKS, viewIdentifier };
}
export function fetchExternalWorksSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< ExternalWork[] >
) : FetchExternalWorksSuccessAction
{
    return { type: FETCH_EXTERNAL_WORKS_SUCCESS, viewIdentifier, payload };
}
export function fetchExternalWorksError( viewIdentifier: string, error: Error ): FetchExternalWorksErrorAction {
    return { type: FETCH_EXTERNAL_WORKS_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchExternalWorksEpic(
    action$: Observable< FetchExternalWorksAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_EXTERNAL_WORKS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchExternalWorksSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType<ExternalWork[]>
                ) ),
                catchError(error => of(
                    fetchExternalWorksError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchExternalWorks")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE EXTERNAL_WORK
 */
export interface FetchExternalWorkByIdAction extends ExternalWorkId, ReduxAction< typeof FETCH_EXTERNAL_WORK_BY_ID> {

}
export interface FetchExternalWorkByIdSuccessAction extends ExternalWorkProp, ReduxAction< typeof FETCH_EXTERNAL_WORK_BY_ID_SUCCESS> {

}
export interface FetchExternalWorkByIdErrorAction extends ExternalWorkId, ErrorAction< typeof FETCH_EXTERNAL_WORK_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchExternalWorkByIdActionTypes = FetchExternalWorkByIdAction
    | FetchExternalWorkByIdSuccessAction
    | FetchExternalWorkByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_EXTERNAL_WORK_BY_ID = "FETCH_EXTERNAL_WORK_BY_ID";
export const FETCH_EXTERNAL_WORK_BY_ID_SUCCESS = "FETCH_EXTERNAL_WORK_BY_ID_SUCCESS";
export const FETCH_EXTERNAL_WORK_BY_ID_ERROR = "FETCH_EXTERNAL_WORK_BY_ID_ERROR";

//Redux action accessors function
export function fetchExternalWorkById( externalWorkId: Identifier<number> ): FetchExternalWorkByIdAction {
    return { type: FETCH_EXTERNAL_WORK_BY_ID, externalWorkId };
}
export function fetchExternalWorkByIdSuccess( externalWork: ExternalWork ): FetchExternalWorkByIdSuccessAction {
    return { type: FETCH_EXTERNAL_WORK_BY_ID_SUCCESS, externalWork };
}
export function fetchExternalWorkByIdError( error: any, externalWorkId: Identifier<number> ): FetchExternalWorkByIdErrorAction {
    return { type: FETCH_EXTERNAL_WORK_BY_ID_ERROR, error, externalWorkId };
}

//redux observable epic as bridge to rxjs
export function fetchExternalWorkByIdEpic(
    action$: Observable< FetchExternalWorkByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_EXTERNAL_WORK_BY_ID ),
        switchMap( action => {
                return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.externalWorkId.rawValue ) )
                ).pipe(
                    map( response => fetchExternalWorkByIdSuccess( response.response as ExternalWork ) ),
                    catchError( error => of(
                        fetchExternalWorkByIdError( error, action.externalWorkId ),
                        eventNotificationError( error, store$, ExternalWorkModule.identifier, "fetchExternalWorkById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */


/**
 * CREATE EXTERNAL_WORK
 */
export interface CreateExternalWorkAction extends ExternalWorkProp, ReduxAction<typeof CREATE_EXTERNAL_WORK> {

}
export interface CreateExternalWorkSuccessAction extends ExternalWorkId, ReduxAction<typeof CREATE_EXTERNAL_WORK_SUCCESS> {

}
export interface CreateExternalWorkErrorAction extends ErrorAction<typeof CREATE_EXTERNAL_WORK_ERROR> {

}
// typescript union type of create actions
export type CreateExternalWorkActionTypes = CreateExternalWorkAction
    | CreateExternalWorkSuccessAction
    | CreateExternalWorkErrorAction

//redux action constants
export const CREATE_EXTERNAL_WORK = "CREATE_EXTERNAL_WORK";
export const CREATE_EXTERNAL_WORK_SUCCESS = "CREATE_EXTERNAL_WORK_SUCCESS";
export const CREATE_EXTERNAL_WORK_ERROR = "CREATE_EXTERNAL_WORK_ERROR";

//redux action accessors functions
export function createExternalWork( externalWork: ExternalWork): CreateExternalWorkAction {
    return { type: CREATE_EXTERNAL_WORK,  externalWork };
}
export function createExternalWorkSuccess( externalWorkId: Identifier<number> ): CreateExternalWorkSuccessAction {
    return { type: CREATE_EXTERNAL_WORK_SUCCESS, externalWorkId };
}
export function createExternalWorkError( error: any ): CreateExternalWorkErrorAction {
    return { type: CREATE_EXTERNAL_WORK_ERROR, error };
}

//redux observable rxjs bridge
export function createExternalWorkEpic(
    action$: Observable< CreateExternalWorkAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_EXTERNAL_WORK ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    action.externalWork
                )
            ).pipe(
                switchMap( response => of(
                    createExternalWorkSuccess( action.externalWork.externalWorkId ),
                    eventNotificationSuccess(),
                    fetchExternalWorks( ExternalWorkModule.identifier ),
                    sendUserToPage(
                        RouteConfig.externalWorks.routes.main.path,
                        ExternalWorkModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createExternalWorkError( error ),
                    eventNotificationError( error, store$, ExternalWorkModule.identifier, "createExternalWork" )
                ))
            )
        )
    )
}

/**
 * UPDATE EXTERNAL_WORK
 */
export interface UpdateExternalWorkAction extends ExternalWorkProp, ReduxAction<typeof UPDATE_EXTERNAL_WORK> {

}
export interface UpdateExternalWorkSuccessAction extends ExternalWorkId, ReduxAction<typeof UPDATE_EXTERNAL_WORK_SUCCESS> {

}
export interface UpdateExternalWorkErrorAction extends ErrorAction<typeof UPDATE_EXTERNAL_WORK_ERROR> {

}
// typescript union type of update actions
export type UpdateExternalWorkActionTypes = UpdateExternalWorkAction
    | UpdateExternalWorkSuccessAction
    | UpdateExternalWorkErrorAction

//redux action constants
export const UPDATE_EXTERNAL_WORK = "UPDATE_EXTERNAL_WORK";
export const UPDATE_EXTERNAL_WORK_SUCCESS = "UPDATE_EXTERNAL_WORK_SUCCESS";
export const UPDATE_EXTERNAL_WORK_ERROR = "UPDATE_EXTERNAL_WORK_ERROR";

//redux action accessors functions
export function updateExternalWork( externalWork: ExternalWork): UpdateExternalWorkAction {
    return { type: UPDATE_EXTERNAL_WORK, externalWork };
}
export function updateExternalWorkSuccess( externalWorkId: Identifier<number> ): UpdateExternalWorkSuccessAction {
    return { type: UPDATE_EXTERNAL_WORK_SUCCESS, externalWorkId };
}
export function updateExternalWorkError( error: any ): UpdateExternalWorkErrorAction {
    return { type: UPDATE_EXTERNAL_WORK_ERROR, error };
}

//redux observable rxjs bridge
export function updateExternalWorkEpic(
    action$: Observable< UpdateExternalWorkAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_EXTERNAL_WORK ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.put,
                    action.externalWork
                )
            ).pipe(
                switchMap( response => of(
                    updateExternalWorkSuccess( action.externalWork.externalWorkId ),
                    eventNotificationSuccess(),
                    fetchExternalWorks( ExternalWorkModule.identifier ),
                    sendUserToPage(
                        RouteConfig.externalWorks.routes.main.path,
                        ExternalWorkModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateExternalWorkError( error ),
                    eventNotificationError( error, store$, ExternalWorkModule.identifier, "updateExternalWork" )
                ))
            )
        )
    )
}

/**
 * DELETE EXTERNAL_WORK
 */
export interface DeleteExternalWorkAction extends ExternalWorkId, ReduxAction<typeof DELETE_EXTERNAL_WORK> {

}
export interface DeleteExternalWorkSuccessAction extends ExternalWorkId, ReduxAction<typeof DELETE_EXTERNAL_WORK_SUCCESS> {

}
export interface DeleteExternalWorkErrorAction extends ErrorAction<typeof DELETE_EXTERNAL_WORK_ERROR> {

}
// typescript union type of delete actions
export type DeleteExternalWorkActionTypes = DeleteExternalWorkAction
    | DeleteExternalWorkSuccessAction
    | DeleteExternalWorkErrorAction

//redux action constants
export const DELETE_EXTERNAL_WORK = "DELETE_EXTERNAL_WORK";
export const DELETE_EXTERNAL_WORK_SUCCESS = "DELETE_EXTERNAL_WORK_SUCCESS";
export const DELETE_EXTERNAL_WORK_ERROR = "DELETE_EXTERNAL_WORK_ERROR";

//redux action accessors functions
export function deleteExternalWork( externalWorkId: Identifier<number>  ): DeleteExternalWorkAction {
    return { type: DELETE_EXTERNAL_WORK, externalWorkId };
}
export function deleteExternalWorkSuccess( externalWorkId: Identifier<number> ): DeleteExternalWorkSuccessAction {
    return { type: DELETE_EXTERNAL_WORK_SUCCESS, externalWorkId };
}
export function deleteExternalWorkError( error: any ): DeleteExternalWorkErrorAction {
    return { type: DELETE_EXTERNAL_WORK_ERROR, error };
}

//redux observable rxjs bridge
export function deleteExternalWorkEpic(
    action$: Observable< DeleteExternalWorkAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_EXTERNAL_WORK ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.externalWorkId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteExternalWorkSuccess( action.externalWorkId ),
                    eventNotificationSuccess(),
                    fetchExternalWorkById( action.externalWorkId )
                ) ),
                catchError( error => of(
                    deleteExternalWorkError( error ),
                    eventNotificationError( error, store$, ExternalWorkModule.identifier, "deleteExternalWork" )
                ))
            )
        )
    )
}

/**
 * RESTORE EXTERNAL_WORK
 */
export interface RestoreExternalWorkAction extends ExternalWorkId, ReduxAction<typeof RESTORE_EXTERNAL_WORK> {

}
export interface RestoreExternalWorkSuccessAction extends ExternalWorkId, ReduxAction<typeof RESTORE_EXTERNAL_WORK_SUCCESS> {

}
export interface RestoreExternalWorkErrorAction extends ErrorAction<typeof RESTORE_EXTERNAL_WORK_ERROR> {

}
// typescript union type of restore actions
export type RestoreExternalWorkActionTypes = RestoreExternalWorkAction
    | RestoreExternalWorkSuccessAction
    | RestoreExternalWorkErrorAction

//redux action constants
export const RESTORE_EXTERNAL_WORK = "RESTORE_EXTERNAL_WORK";
export const RESTORE_EXTERNAL_WORK_SUCCESS = "RESTORE_EXTERNAL_WORK_SUCCESS";
export const RESTORE_EXTERNAL_WORK_ERROR = "RESTORE_EXTERNAL_WORK_ERROR";

//redux action accessors functions
export function restoreExternalWork( externalWorkId: Identifier<number>  ): RestoreExternalWorkAction {
    return { type: RESTORE_EXTERNAL_WORK, externalWorkId };
}
export function restoreExternalWorkSuccess( externalWorkId: Identifier<number> ): RestoreExternalWorkSuccessAction {
    return { type: RESTORE_EXTERNAL_WORK_SUCCESS, externalWorkId };
}
export function restoreExternalWorkError( error: any ): RestoreExternalWorkErrorAction {
    return { type: RESTORE_EXTERNAL_WORK_ERROR, error };
}

//redux observable rxjs bridge
export function restoreExternalWorkEpic(
    action$: Observable< RestoreExternalWorkAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_EXTERNAL_WORK ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.externalWorkId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreExternalWorkSuccess( action.externalWorkId ),
                    eventNotificationSuccess(),
                    fetchExternalWorkById( action.externalWorkId )
                ) ),
                catchError( error => of(
                    restoreExternalWorkError( error ),
                    eventNotificationError( error, store$, ExternalWorkModule.identifier, "restoreExternalWork" )
                ))
            )
        )
    )
}

export interface GenerateExternalWorkTasksAction extends ReduxAction<typeof GENERATE_EXTERNAL_WORK_TASKS> {
    work: ExternalWork
}
export interface GenerateExternalWorkTasksSuccessAction extends ReduxAction<typeof GENERATE_EXTERNAL_WORK_TASKS_SUCCESS> {

}
export interface GenerateExternalWorkTasksErrorAction extends ErrorAction<typeof GENERATE_EXTERNAL_WORK_TASKS_ERROR> {

}
// typescript union type of restore actions
export type GenerateExternalWorkTasksActionTypes = GenerateExternalWorkTasksAction
    | GenerateExternalWorkTasksSuccessAction
    | GenerateExternalWorkTasksErrorAction

//redux action constants
export const GENERATE_EXTERNAL_WORK_TASKS = "GENERATE_EXTERNAL_WORK_TASKS";
export const GENERATE_EXTERNAL_WORK_TASKS_SUCCESS = "GENERATE_EXTERNAL_WORK_TASKS_SUCCESS";
export const GENERATE_EXTERNAL_WORK_TASKS_ERROR = "GENERATE_EXTERNAL_WORK_TASKS_ERROR";


export function generateExternalWorkTasks( work: ExternalWork ): GenerateExternalWorkTasksAction {
    return {
        type: GENERATE_EXTERNAL_WORK_TASKS,
        work
    };
}
export function generateExternalWorkTasksSuccess(): GenerateExternalWorkTasksSuccessAction {
    return {
        type: GENERATE_EXTERNAL_WORK_TASKS_SUCCESS
    };
}
export function generateExternalWorkTasksError( error: any ): GenerateExternalWorkTasksErrorAction {
    return {
        type: GENERATE_EXTERNAL_WORK_TASKS_ERROR,
        error
    };
}

export function generateExternalWorkTasksEpic(
    action$: Observable< GenerateExternalWorkTasksAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( GENERATE_EXTERNAL_WORK_TASKS ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath()+ "generate_tasks/"+ action.work.externalWorkId.rawValue,
                )
            ).pipe(
                switchMap( _ => of(
                    generateExternalWorkTasksSuccess(),
                    fetchInitialSelectOptions(
                        ExternalWorkModule.identifier,
                        timesheetTaskSelectRoute(),
                        {
                            ...getInitialViewSettings( ExternalWorkModule.identifier ),
                            searchValue: action.work.name
                        }
                    ),
                    eventNotificationSuccess(),
                ) ),
                catchError( error => of(
                    generateExternalWorkTasksError( error ),
                    eventNotificationError( error, store$, ExternalWorkModule.identifier, "restoreExternalWork" )
                ))
            )
        )
    )
}